import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { FileText, Download, RefreshCw, FileSearch, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_SECTIONS = [
  { id: 'phi_access_log',          label: 'PHI access log' },
  { id: 'audit_events',             label: 'Audit events' },
  { id: 'clinical_notes_history',   label: 'Clinical-note history' },
  { id: 'credential_verifications', label: 'Credential verifications' },
  { id: 'baa_signatures',           label: 'BAA signatures' },
];

const todayIso = () => new Date().toISOString().slice(0, 10) + 'T23:59:59+00:00';
const daysAgoIso = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10) + 'T00:00:00+00:00';
};

/** /compliance/audit-export — Audit Trail Export (Blueprint #16). */
export default function AuditExport() {
  const { getAuthHeader } = useAuth();
  const [start, setStart] = useState(daysAgoIso(30));
  const [end, setEnd] = useState(todayIso());
  const [sections, setSections] = useState({
    phi_access_log: true, audit_events: true, clinical_notes_history: true,
    credential_verifications: false, baa_signatures: false,
  });
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance/audit-export`, getAuthHeader());
      setJobs(data.jobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load audit-export jobs');
    }
  }, [getAuthHeader]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const runExport = async () => {
    const picked = Object.entries(sections).filter(([, v]) => v).map(([k]) => k);
    if (picked.length === 0) { toast.error('Pick at least one section'); return; }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/compliance/audit-export/request`, {
        start_date: start, end_date: end, sections: picked, format: 'json',
      }, getAuthHeader());
      setLastResult(data);
      toast.success(`Export job created — ${Object.values(data.section_counts).reduce((a, b) => a + b, 0)} records`);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setRunning(false);
    }
  };

  const download = async (jobId, format) => {
    try {
      const { data, headers } = await axios.get(`${API}/compliance/audit-export/${jobId}?format=${format}`, {
        ...getAuthHeader(),
        responseType: format === 'csv' ? 'blob' : 'json',
      });
      const blob = format === 'csv' ? data : new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `audit-export-${jobId}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  return (
    <DashboardShell data-testid="audit-export-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cn-eyebrow inline-flex items-center gap-1.5">
            <FileSearch className="w-3 h-3" /> Compliance · Audit-trail export
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Audit-trail export</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Generate a Medicare-ZPIC-aligned audit package for any date range. Includes every PHI read, every clinical-note change, every credentialing event in your org.
          </p>
        </div>
        <Button variant="outline" onClick={loadJobs}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </header>

      {/* Request form */}
      <section className="rounded-2xl bg-white border border-slate-100 p-6 mb-6" data-testid="audit-export-form">
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">New export job</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Start</span>
            <input type="datetime-local" value={start.slice(0, 16)}
              onChange={(e) => setStart(e.target.value + ':00+00:00')}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cn-primary"
              data-testid="audit-export-start" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">End</span>
            <input type="datetime-local" value={end.slice(0, 16)}
              onChange={(e) => setEnd(e.target.value + ':00+00:00')}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cn-primary"
              data-testid="audit-export-end" />
          </label>
        </div>
        <div className="mb-4">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">Sections to include</span>
          <div className="flex flex-wrap gap-2" data-testid="audit-export-sections">
            {ALL_SECTIONS.map((s) => (
              <label key={s.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition ${
                sections[s.id]
                  ? 'bg-cn-primary text-white border-cn-primary'
                  : 'bg-white border-slate-200 hover:border-cn-primary/50'
              }`}>
                <input type="checkbox" checked={sections[s.id] || false}
                  onChange={(e) => setSections({ ...sections, [s.id]: e.target.checked })}
                  className="hidden"
                  data-testid={`audit-export-section-${s.id}`} />
                {s.label}
              </label>
            ))}
          </div>
        </div>
        <Button onClick={runExport} disabled={running} className="bg-cn-primary text-white hover:bg-cn-primary-hover" data-testid="audit-export-run">
          {running ? 'Generating…' : 'Generate export'}
        </Button>
        {lastResult && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900 flex items-center gap-2" data-testid="audit-export-last-result">
            <CheckCircle2 className="w-4 h-4" />
            Job {lastResult.id.slice(0, 8)} created · {Object.entries(lastResult.section_counts).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ')}
          </div>
        )}
      </section>

      {/* Jobs list */}
      <section className="rounded-2xl bg-white border border-slate-100 overflow-hidden" data-testid="audit-export-jobs">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Recent export jobs</h2>
          <span className="text-xs text-slate-500">{jobs.length} jobs</span>
        </div>
        {jobs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">No export jobs yet.</p>
            <p className="text-sm text-slate-500 mt-1">Generate your first export above.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold">Created</th>
                <th className="text-left p-3 font-semibold">Range</th>
                <th className="text-left p-3 font-semibold">Sections</th>
                <th className="text-right p-3 font-semibold">Records</th>
                <th className="text-right p-3 font-semibold">Download</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-slate-100 last:border-0" data-testid={`audit-export-job-${j.id}`}>
                  <td className="p-3 text-slate-600 text-xs">{new Date(j.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs text-slate-700">
                    {new Date(j.start_date).toLocaleDateString()} → {new Date(j.end_date).toLocaleDateString()}
                  </td>
                  <td className="p-3 text-xs text-slate-700">{(j.sections || []).join(', ')}</td>
                  <td className="p-3 text-right tabular-nums font-semibold">
                    {Object.values(j.section_counts || {}).reduce((a, b) => a + b, 0)}
                  </td>
                  <td className="p-3 text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => download(j.id, 'json')} data-testid={`audit-export-dl-json-${j.id}`}>
                      <Download className="w-3 h-3 mr-1" /> JSON
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => download(j.id, 'csv')} data-testid={`audit-export-dl-csv-${j.id}`}>
                      <Download className="w-3 h-3 mr-1" /> CSV
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </DashboardShell>
  );
}
